import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { css } from "styled-components";

interface OfferingModel {
    fields: {
        title: string;
        background: "image";
        image: string;
        description: string;
        destination: LinkFieldValue;
        linkTitle: string;
    };
}

const enum fields {
    offerings = "offerings",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.offerings]: OfferingModel[];
    };
    extendedStyles?: OfferingStyles;
}

export interface OfferingStyles {
    link?: LinkProps;
    image?: LazyImageProps;
    titleText?: TypographyProps;
    offeringTitle: TypographyProps;
    gridWrapper?: GridContainerProps;
    gridItem?: GridItemProps;
    descriptionWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    description?: TypographyProps;
}

export const offeringStyles: OfferingStyles = {
    titleText: {
        variant: "h3",
        transform: "uppercase",
        css: css`
            border-width: 20px;
            text-align: center;
            margin: 0;
            padding: 30px 0;
        `,
    },
    link: {
        css: css`
            display: flex;
            position: relative;
            color: ${COLORS.link.base};
            font-size: ${TYPOGRAPHY.size.small};
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    offeringTitle: {
        css: css`
            margin: 0;
            width: 100%;
            padding-bottom: 10px;
            font-size: ${TYPOGRAPHY.size.largest};
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    gridWrapper: {
        gap: 20,
        css: css`
            flex-wrap: wrap;
        `,
    },
    gridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            align-items: center;
            flex-direction: column;
        `,
    },
    descriptionWrapper: {
        css: css`
            padding: 30px 20px;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        `,
    },
    cardWrapper: {
        css: css`
            background: ${COLORS.white};
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            border-radius: 10px;
            width: 100%;
            &:hover {
                box-shadow: 0px 3px 20px #00000029;
                img {
                    transition: all 0.5s;
                    transform: scale(1.03);
                }
            }
        `,
    },
    image: {
        css: css`
            height: auto;
            img {
                width: 100%;
            }
        `,
    },
    description: {
        css: css`
            flex-grow: 1;
            margin-bottom: 20px;
            font-size: ${TYPOGRAPHY.size.base};
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        pageLink: ownProps.fields.offerings.map(card => getLink(state, card.fields.destination)),
    };
};

const mapDispatchToProps = {};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const Solutions: React.FC<Props> = ({ fields, pageLink, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(offeringStyles, extendedStyles));

    return (
        <GridContainer {...styles.gridWrapper}>
            {fields.offerings &&
                fields.offerings.length &&
                fields.offerings.map((offering, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <GridItem {...styles.gridItem} key={index}>
                        <StyledWrapper {...styles.cardWrapper}>
                            <LazyImage {...styles.image} src={offering.fields.image} />
                            <StyledWrapper {...styles.descriptionWrapper}>
                                <Typography {...styles.offeringTitle}>{translate(offering?.fields?.title)}</Typography>
                                <Typography {...styles.description}>
                                    {translate(offering?.fields?.description)}
                                </Typography>
                                <Clickable href={pageLink[index]?.url} {...styles.link} target="_blank">
                                    {translate(offering?.fields?.linkTitle)}
                                </Clickable>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                ))}
        </GridContainer>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Solutions),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.offerings,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
